import React from 'react';
import classnames from 'classnames';
import VirtualizedGrid from 'react-virtualized/dist/commonjs/Grid';
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer';

import AssetItem from './item-asset';
import UnsplashItem from './item-unsplash';

import emptyLibrarySvg from 'ub/icons/asset-library/empty-library.svg';
import { assetSourcePropType, itemsPropType, sortByPropType } from './prop-types';

import styles from './asset-grid.scss';

export default class AssetGrid extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = { verticalScrollBarWidth: 0 };

    this.handleItemDoubleClick = this.handleItemDoubleClick.bind(this);
    this.handleStartDelete = this.handleStartDelete.bind(this);
    this.handleScrollBarChange = this.handleScrollBarChange.bind(this);
  }

  renderGridCell(columnCount, { columnIndex, rowIndex, key, style, isVisible, isScrolling }) {
    const item = this.props.items[rowIndex * columnCount + columnIndex];

    const allowDelete = isVisible && !isScrolling && this.props.allowDelete;

    if (!item) {
      return null;
    }

    const commonProps = {
      filename: item.filename,
      isSelected: this.props.selectedItemIds.includes(item.id),
      key: key,
      onClick: isVisible ? this.handleSelectItem.bind(this, item) : undefined,
      onDelete: allowDelete ? this.handleStartDelete : undefined,
      onDoubleClick: isVisible ? this.handleItemDoubleClick : undefined,
      style: style,
      thumbnailImage: this.getThumbnailImage(item),
    };

    if (this.props.selectedSource === 'unsplash') {
      return (
        <UnsplashItem
          {...commonProps}
          authorLink={item.unsplash.authorLink}
          authorName={item.unsplash.authorName}
          description={item.unsplash.description}
          link={item.unsplash.link}
        />
      );
    } else {
      return <AssetItem {...commonProps} />;
    }
  }

  handleSelectItem(selectedItem, event) {
    this.props.dispatch({
      type: 'selectItem',
      itemId: selectedItem.id,
      ctrlKey: event.ctrlKey,
      metaKey: event.metaKey,
      shiftKey: event.shiftKey,
    });
  }

  handleItemDoubleClick() {
    this.props.dispatch({ type: 'choose' });
  }

  handleStartDelete() {
    this.props.dispatch({ type: 'startDelete' });
  }

  handleSectionRendered(rowCount, { rowStopIndex }) {
    // This function is called with details of the visible grid cells rendered. When we have
    // rendered a row that is within 2 rows of the end, call the onScrollNearBottom which will
    // trigger another set of items to be fetched if available. We are doing this using the
    // onSectionRendered prop instead of adding a scroll handler because this also allows us to
    // immediately call onScrollNearBottom if necessary, without the user having to scroll first.
    if (rowStopIndex + 1 > rowCount - 2) {
      this.props.onScrollNearBottom();
    }
  }

  handleScrollBarChange({ vertical, size }) {
    this.setState({ verticalScrollBarWidth: vertical ? size : 0 });
  }

  getThumbnailImage(item) {
    if (item.asset && item.asset.content_content_type === 'image/svg+xml') {
      return item.fullUrl;
    } else if (item.type === 'images') {
      return item.thumbnailUrl;
    } else {
      return null;
    }
  }

  getScrollToRow(columns) {
    const { items, selectedItemIds } = this.props;

    if (selectedItemIds.length === 0) {
      return -1;
    }

    const selectedItemIndex = items.findIndex(asset => asset.id === selectedItemIds[0]);
    return selectedItemIndex >= 0 ? Math.floor(selectedItemIndex / columns) : -1;
  }

  getErrorMessage() {
    const { fetchError, emptyMessage, isEmpty } = this.props;

    if (fetchError) {
      return (
        'We were unable to load any files. ' +
        'Please try again or contact support if the problem persists.'
      );
    } else if (isEmpty) {
      return emptyMessage;
    } else {
      return null;
    }
  }

  render() {
    const {
      allowUpload,
      items,
      largeThumbnails,
      selectedItemIds,
      sortAscending,
      sortBy,
    } = this.props;
    const { verticalScrollBarWidth } = this.state;

    const errorMessage = this.getErrorMessage();

    const cellHeight = largeThumbnails ? 250 : 160;
    const cellWidth = largeThumbnails ? 200 : 130;

    return (
      <div className="asset-pane">
        <div className={classnames('asset-pane-contents', styles.assetPaneContents)}>
          {errorMessage ? (
            <div className="note js-no-asset-notice no-asset-notice">
              <div
                className="asset-note-icon"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: emptyLibrarySvg }}
              />

              <div className="asset-note-headline">{errorMessage}</div>

              {allowUpload && (
                <div>Click the button below to upload new files from your computer.</div>
              )}
            </div>
          ) : (
            // VirtualizedGrid requires explicit width and height values so that it can perform its
            // virtualization/windowing. The AutoSizer component calculates the dimensions that will
            // fill the container, calls the child function with them, and renders its return
            // value.
            <AutoSizer>
              {({ width, height }) => {
                const adjustedWidth = width - verticalScrollBarWidth;
                const columnCount = Math.floor(adjustedWidth / cellWidth);
                const rowCount = Math.ceil(items.length / columnCount);

                // react-virtualized is an optimization that only renders the items that are
                // visible for the current scroll position, plus some overscan rows so that
                // scrolling isn't janky. Since there is no limit to how many assets a client can
                // have, this drastically improves performance of asset library loading, sorting,
                // and searching.
                return (
                  <VirtualizedGrid
                    cellRenderer={args => this.renderGridCell(columnCount, args)}
                    columnCount={columnCount}
                    columnWidth={adjustedWidth / columnCount}
                    height={height}
                    onScrollbarPresenceChange={this.handleScrollBarChange}
                    onSectionRendered={args => this.handleSectionRendered(rowCount, args)}
                    overscanColumnCount={0}
                    overscanRowCount={4}
                    rowCount={rowCount}
                    rowHeight={cellHeight}
                    scrollToRow={this.getScrollToRow(columnCount)}
                    width={width}
                    // The following props are not used by VirtualizedGrid but are passed so
                    // that when they change, the grid updates. See
                    // https://github.com/bvaughn/react-virtualized#pure-components
                    itemCount={items.length}
                    sortBy={sortBy}
                    sortAscending={sortAscending}
                    selectedItemIds={selectedItemIds}
                  />
                );
              }}
            </AutoSizer>
          )}
        </div>
      </div>
    );
  }
}

AssetGrid.propTypes = {
  allowDelete: React.PropTypes.bool.isRequired,
  allowUpload: React.PropTypes.bool.isRequired,
  dispatch: React.PropTypes.func.isRequired,
  emptyMessage: React.PropTypes.string.isRequired,
  fetchError: React.PropTypes.instanceOf(Error),
  isEmpty: React.PropTypes.bool.isRequired,
  items: itemsPropType.isRequired,
  largeThumbnails: React.PropTypes.bool.isRequired,
  onScrollNearBottom: React.PropTypes.func.isRequired,
  selectedItemIds: React.PropTypes.arrayOf(React.PropTypes.string).isRequired,
  selectedSource: assetSourcePropType.isRequired,
  sortAscending: React.PropTypes.bool.isRequired,
  sortBy: sortByPropType.isRequired,
};



// WEBPACK FOOTER //
// ./src/components/asset-library/asset-grid.jsx